import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import '@/App.css';
import { Toaster } from '@/components/ui/sonner';
import { CartProvider } from '@/context/CartContext';
import { AuthProvider } from '@/context/AuthContext';
import { AddressProvider } from '@/context/AddressContext';
import { WishlistProvider } from '@/context/WishlistContext';
import { RecentlyViewedProvider } from '@/context/RecentlyViewedContext';
import CustomerLayout from '@/components/layout/CustomerLayout';
import AdminLayout from '@/components/layout/AdminLayout';
import BottomNavBar from '@/components/layout/BottomNavBar';
import HomePage from '@/pages/HomePage';
import ProductsPage from '@/pages/ProductsPage';
import ProductDetailPage from '@/pages/ProductDetailPage';
import CheckoutPage from '@/pages/CheckoutPage';
import PaymentSuccessPage from '@/pages/PaymentSuccessPage';
import OrderSuccessPage from '@/pages/OrderSuccessPage';
import CategoriesPage from '@/pages/CategoriesPage';
import DealsPage from '@/pages/DealsPage';
import MyOrdersPage from '@/pages/MyOrdersPage';
import OrderTrackingPage from '@/pages/OrderTrackingPage';
import WishlistPage from '@/pages/WishlistPage';
import SupportPage from '@/pages/SupportPage';
import AboutPage from '@/pages/AboutPage';
import CartPage from '@/pages/CartPage';
import ProfilePage from '@/pages/ProfilePage';
import AccountPage from '@/pages/AccountPage';
import ContactPage from '@/pages/ContactPage';
import LoginPage from '@/pages/LoginPage';
import OTPLoginPage from '@/pages/OTPLoginPage';
import AuthCallbackPage from '@/pages/AuthCallbackPage';
import AdminDashboardPage from '@/pages/admin/AdminDashboardPage';
import AdminProductsPage from '@/pages/admin/AdminProductsPage';
import AdminOrdersPage from '@/pages/admin/AdminOrdersPage';
import AdminBrandsPage from '@/pages/admin/AdminBrandsPage';
import AdminCategoriesPage from '@/pages/admin/AdminCategoriesPage';
import AdminRequestsPage from '@/pages/admin/AdminRequestsPage';
import AdminBannersPage from '@/pages/admin/AdminBannersPage';
import AdminUsersPage from '@/pages/admin/AdminUsersPage';
import AdminPromocodesPage from '@/pages/admin/AdminPromocodesPage';
import RequestItemPage from '@/pages/RequestItemPage';
import DeliveryInfoPage from '@/pages/DeliveryInfoPage';
import AdminLoginPage from '@/pages/AdminLoginPage';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import CustomerProtectedRoute from '@/components/auth/CustomerProtectedRoute';
import { useEffect } from 'react';

// Router component that handles OAuth callback detection synchronously
function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Detect session_id in hash fragment SYNCHRONOUSLY during render
  // This prevents race conditions - hash must be processed BEFORE other routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallbackPage />;
  }
  
  return (
    <Routes>
      {/* Customer Routes with Sidebar */}
      <Route path="/" element={<CustomerLayout><HomePage /></CustomerLayout>} />
      <Route path="/products" element={<CustomerLayout><ProductsPage /></CustomerLayout>} />
      <Route path="/product/:id" element={<CustomerLayout><ProductDetailPage /></CustomerLayout>} />
      <Route path="/categories" element={<CustomerLayout><CategoriesPage /></CustomerLayout>} />
      <Route path="/deals" element={<CustomerLayout><DealsPage /></CustomerLayout>} />
      <Route path="/account" element={<CustomerLayout><AccountPage /></CustomerLayout>} />
      <Route path="/my-orders" element={<CustomerProtectedRoute><CustomerLayout><MyOrdersPage /></CustomerLayout></CustomerProtectedRoute>} />
      <Route path="/order/:orderId" element={<CustomerProtectedRoute><CustomerLayout><OrderTrackingPage /></CustomerLayout></CustomerProtectedRoute>} />
      <Route path="/wishlist" element={<CustomerProtectedRoute><CustomerLayout><WishlistPage /></CustomerLayout></CustomerProtectedRoute>} />
      <Route path="/support" element={<CustomerLayout><SupportPage /></CustomerLayout>} />
      <Route path="/about" element={<CustomerLayout><AboutPage /></CustomerLayout>} />
      <Route path="/cart" element={<CustomerLayout><CartPage /></CustomerLayout>} />
      <Route path="/profile" element={<CustomerProtectedRoute><CustomerLayout><ProfilePage /></CustomerLayout></CustomerProtectedRoute>} />
      <Route path="/contact" element={<CustomerLayout><ContactPage /></CustomerLayout>} />
      <Route path="/request-item" element={<RequestItemPage />} />
      <Route path="/delivery-info" element={<DeliveryInfoPage />} />
      <Route path="/checkout" element={<CustomerLayout><CheckoutPage /></CustomerLayout>} />
      <Route path="/payment-success" element={<CustomerLayout><PaymentSuccessPage /></CustomerLayout>} />
      <Route path="/order-success/:orderId" element={<CustomerLayout><OrderSuccessPage /></CustomerLayout>} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/otp-login" element={<OTPLoginPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      
      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <AdminLayout><AdminDashboardPage /></AdminLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/products" 
        element={
          <ProtectedRoute>
            <AdminLayout><AdminProductsPage /></AdminLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/orders" 
        element={
          <ProtectedRoute>
            <AdminLayout><AdminOrdersPage /></AdminLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/brands" 
        element={
          <ProtectedRoute>
            <AdminLayout><AdminBrandsPage /></AdminLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/categories" 
        element={
          <ProtectedRoute>
            <AdminLayout><AdminCategoriesPage /></AdminLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/requests" 
        element={
          <ProtectedRoute>
            <AdminLayout><AdminRequestsPage /></AdminLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/banners" 
        element={
          <ProtectedRoute>
            <AdminLayout><AdminBannersPage /></AdminLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/users" 
        element={
          <ProtectedRoute>
            <AdminLayout><AdminUsersPage /></AdminLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/promocodes" 
        element={
          <ProtectedRoute>
            <AdminLayout><AdminPromocodesPage /></AdminLayout>
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
}

function App() {
  // Suppress ResizeObserver errors (common with Radix UI)
  useEffect(() => {
    const resizeObserverErr = window.console.error;
    window.console.error = (...args) => {
      if (args[0]?.includes?.('ResizeObserver')) {
        return;
      }
      resizeObserverErr(...args);
    };
  }, []);

  return (
    <AuthProvider>
      <AddressProvider>
        <WishlistProvider>
          <RecentlyViewedProvider>
          <CartProvider>
            <div className="App">
              <BrowserRouter>
                <AppRouter />
                <BottomNavBar />
                <Toaster position="top-center" />
              </BrowserRouter>
            </div>
          </CartProvider>
          </RecentlyViewedProvider>
        </WishlistProvider>
      </AddressProvider>
    </AuthProvider>
  );
}

export default App;